/**
 * Report layout builders used before adapting Report export items to the canonical presentation model.
 * They normalize card/tree data and derive reusable layout details such as columns and connectors.
 */

// Colors are authored as CSS hex ('#B3C9CB') but PptxGenJS wants them bare ('B3C9CB').
const toHex = (color, fallback = '000000') => String(color ?? fallback).replace(/^#/, '');

/**
 * @typedef {Object} ExportItemBase
 * @property {Number} [order=0] sort key within a section (ascending)
 * @property {String} [title]   slide title
 */

/**
 * Generic text slide for already-derived, presentation-ready content.
 * @param {Object} params
 * @param {String} params.body
 * @returns {Object} text ExportItem
 */
export const textItem = ({ body, order = 0, title }) => {
  if (typeof body !== 'string' || !body.trim()) {
    throw new TypeError('textItem: `body` must be a non-empty string');
  }
  return { body, order, title, type: 'text' };
};

/**
 * Key Takeaways slide. The body may contain ordinary text or Markdown-style bullet lists.
 * @param {Object} params
 * @param {String} params.body
 * @returns {Object} takeaways ExportItem
 */
export const takeawaysItem = ({ body, order = 0, title = 'Key Takeaways' }) => {
  if (typeof body !== 'string' || !body.trim()) {
    throw new TypeError('takeawaysItem: `body` must be a non-empty string');
  }
  return { body, order, title, type: 'takeaways' };
};

/**
 * @typedef {Object} CardGridCard
 * @property {Number} [row=0] 0-based grid row
 * @property {Number} [col]   0-based grid column (defaults to array index)
 * @property {String} [color] accent color, hex with or without a leading '#'
 * @property {String} [title]
 * @property {String} [description]
 * @property {Array<{label:String,value:String}>} [rows] metric rows drawn inside the card
 */

/**
 * Card-grid slide (e.g. a Penetration Grid): a uniform grid of accented cards, each with a title,
 * optional description, and { label, value } rows. `columns` defaults to the widest populated column.
 * @param {Object} params
 * @param {CardGridCard[]} params.cards
 * @param {Number} [params.columns] cards per row (derived from data when omitted)
 * @param {Boolean} [params.fitOnSingleSlide=false] fit all logical rows on one slide
 * @returns {Object} cards ExportItem
 */
export const cardGridItem = ({
  cards = [],
  columns,
  fitOnSingleSlide = false,
  order = 0,
  title,
}) => {
  const normalized = cards.map((card, index) => ({
    col: card?.col ?? index,
    color: toHex(card?.color, 'B3C9CB'),
    description: card?.description || '',
    row: card?.row ?? 0,
    rows: (card?.rows || []).map((row) => ({ label: row?.label || '', value: row?.value || '' })),
    title: card?.title || `Card ${index + 1}`,
  }));
  const resolvedColumns =
    columns || normalized.reduce((max, card) => Math.max(max, card.col + 1), 1);
  return {
    cards: normalized,
    columns: resolvedColumns,
    fitOnSingleSlide,
    order,
    title,
    type: 'cards',
  };
};

/**
 * @typedef {Object} TreeNode
 * @property {String} nodeId     unique id (used to wire connectors)
 * @property {Number} [row=1]    1-based tree row
 * @property {Number} [gridSpan=4] width in 12ths
 * @property {Number} [leadingOffset=0] gap columns before this node (Bootstrap-style offset)
 * @property {Number} [col]      explicit 0-based column; when omitted it is computed by column-flow
 * @property {String} [color]    accent color, hex with or without a leading '#'
 * @property {String} [title]
 * @property {String} [description]
 * @property {Array<{left:String,right:String}>} [rows] label/value rows drawn inside the node card
 */

/**
 * Flowing 12-column layout: within each row, nodes are placed left-to-right; `leadingOffset` inserts
 * gap columns before a node, which cascades a decomposition tree rightward row by row. Mutates each
 * node with a computed `col` (respecting an explicit `col` if already set) and returns nodes grouped
 * by row. Exported so features can lay out custom trees before wiring their own connectors.
 * @param {TreeNode[]} nodes
 * @returns {Object<Number, TreeNode[]>} nodes grouped by row
 */
export const layoutTreeNodes = (nodes = []) => {
  const byRow = nodes.reduce((acc, node) => {
    (acc[node.row] = acc[node.row] || []).push(node);
    return acc;
  }, {});

  Object.keys(byRow)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((row) => {
      let cursor = 0;
      byRow[row].forEach((node) => {
        node.col = node.col ?? cursor + (node.leadingOffset || 0);
        cursor = node.col + (node.gridSpan || 4);
      });
    });

  return byRow;
};

/**
 * Derives parent -> children connectors for a decomposition ("spine") tree: each row's nodes descend
 * from a single node of the previous row. `anchor` picks which end continues the spine:
 *   - `'last'` (default): the LAST node decomposes — e.g. a Brand Tree (Sales -> [Households, Buy Rate];
 *     Buy Rate -> [Frequency, Spend per Trip]).
 *   - `'first'`: the FIRST node decomposes — e.g. a Leakage Tree (All Shoppers -> [Store, Not Store];
 *     Store Shoppers -> [Buyer, Non-Buyer]; …), which cascades down the left/primary branch.
 * @param {Object<Number, TreeNode[]>} nodesByRow
 * @param {Object} [options]
 * @param {'first'|'last'} [options.anchor='last']
 * @returns {Array<{from:String,to:String[]}>}
 */
export const buildSpineConnectors = (nodesByRow, { anchor = 'last' } = {}) => {
  const rowNumbers = Object.keys(nodesByRow)
    .map(Number)
    .sort((a, b) => a - b);

  return rowNumbers.slice(1).reduce((connectors, rowNumber, index) => {
    const previousRow = nodesByRow[rowNumbers[index]];
    const parent = anchor === 'first' ? previousRow[0] : previousRow[previousRow.length - 1];
    const children = nodesByRow[rowNumber];
    if (parent && children?.length) {
      connectors.push({ from: parent.nodeId, to: children.map((child) => child.nodeId) });
    }
    return connectors;
  }, []);
};

/**
 * Tree slide (e.g. a Brand Tree): accented node cards positioned on a flowing 12-column grid and joined
 * by elbow connectors. Positions are computed from each node's { row, gridSpan, leadingOffset } via
 * column-flow; connectors default to the decomposition spine unless supplied. This is the one consistent
 * way any feature builds a native tree — pass clean nodes, get a render-ready item.
 * @param {Object} params
 * @param {TreeNode[]} params.nodes
 * @param {Array<{from:String,to:String[]}>} [params.connectors] explicit connectors (else derived spine)
 * @param {'first'|'last'} [params.spineAnchor='last'] which end of each row decomposes (see buildSpineConnectors)
 * @returns {Object} tree ExportItem
 */
export const treeItem = ({ connectors, nodes = [], order = 0, spineAnchor = 'last', title }) => {
  const positioned = nodes
    .filter((node) => node?.nodeId)
    .map((node) => ({
      col: node.col,
      color: toHex(node.color, 'B3C9CB'),
      description: node.description || '',
      gridSpan: node.gridSpan || 4,
      leadingOffset: node.leadingOffset || 0,
      nodeId: node.nodeId,
      row: node.row || 1,
      rows: (node.rows || []).map((row) => ({ left: row?.left || '', right: row?.right || '' })),
      title: node.title || node.nodeId,
    }));

  const nodesByRow = layoutTreeNodes(positioned);

  return {
    connectors: connectors || buildSpineConnectors(nodesByRow, { anchor: spineAnchor }),
    nodes: positioned,
    order,
    title,
    type: 'tree',
  };
};
